import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  User, Shield, Building2, Briefcase, Camera, Loader2, 
  CheckCircle, MapPin, Clock, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Account category definitions
const ACCOUNT_CATEGORIES = [
  {
    id: 'responsible_citizen',
    title: 'Responsible Citizen',
    description: 'Engage with civic issues, report problems, and participate in community discussions.',
    icon: User,
    available: true,
    color: 'from-blue-500 to-indigo-600'
  },
  {
    id: 'leader',
    title: 'Leader',
    description: 'Connect with constituents, respond to issues, and showcase your work as a political representative.',
    icon: Building2,
    available: false,
    color: 'from-purple-500 to-pink-600'
  },
  {
    id: 'government_department',
    title: 'Government Department',
    description: 'Respond to citizen concerns, provide updates, and manage public service communications.',
    icon: Shield,
    available: false,
    color: 'from-green-500 to-emerald-600'
  },
  {
    id: 'media',
    title: 'Media',
    description: 'Report on civic issues, verify information, and share news with the community.',
    icon: Briefcase,
    available: false,
    color: 'from-amber-500 to-orange-600'
  }
];

const ProfileOnboarding = ({ 
  isOpen, 
  onClose, 
  onComplete, 
  user,
  prefillData = null,
  forceCategory = false // If true, user must select category even if other fields complete
}) => {
  const [step, setStep] = useState('category'); // 'category', 'form', 'coming-soon'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [completionStatus, setCompletionStatus] = useState(null);
  
  // Geography data
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [loadingGeo, setLoadingGeo] = useState({ states: false, districts: false, constituencies: false });
  const [hasConstituencyData, setHasConstituencyData] = useState(false);
  
  // Form data - prefilled from existing user data
  const [formData, setFormData] = useState({
    name: '',
    state: '',
    district: '',
    constituency: ''
  });
  
  // Profile photo
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  // Check completion status on mount
  useEffect(() => {
    if (isOpen && user) {
      checkCompletionStatus();
    }
  }, [isOpen, user]);

  // Fetch states on mount
  useEffect(() => {
    if (isOpen) {
      fetchStates();
    }
  }, [isOpen]);

  // Fetch districts when state changes
  useEffect(() => {
    if (formData.state) {
      fetchDistricts(formData.state);
    } else {
      setDistricts([]);
      setConstituencies([]);
      setHasConstituencyData(false);
    }
  }, [formData.state]);

  // Fetch constituencies when district changes
  useEffect(() => {
    if (formData.state && formData.district) {
      fetchConstituencies(formData.state, formData.district);
    } else {
      setConstituencies([]);
      setHasConstituencyData(false);
    }
  }, [formData.district]);

  const checkCompletionStatus = async () => {
    setCheckingStatus(true);
    try {
      const response = await axios.get(`${API_URL}/api/profile/completion-status`, {
        withCredentials: true
      });
      setCompletionStatus(response.data);
      
      // Prefill form with existing data
      const prefill = response.data.prefill || {};
      setFormData({
        name: prefill.name || '',
        state: prefill.state || '',
        district: prefill.district || '',
        constituency: prefill.constituency || ''
      });
      
      if (prefill.picture) {
        setPhotoPreview(prefill.picture);
      }
      
      // Determine starting step
      if (response.data.profile_completion && !forceCategory) {
        // Profile is complete, close modal
        onComplete && onComplete();
        onClose();
      } else if (response.data.fields?.account_category?.complete && !forceCategory) {
        // Category selected but other fields missing - go to form
        setSelectedCategory('responsible_citizen');
        setStep('form');
      } else {
        // Need to select category
        setStep('category');
      }
    } catch (error) {
      console.error('Error checking completion status:', error);
      // Default to category selection on error
      setStep('category');
    } finally {
      setCheckingStatus(false);
    }
  };

  const fetchStates = async () => {
    setLoadingGeo(prev => ({ ...prev, states: true }));
    try {
      const response = await axios.get(`${API_URL}/api/geography/states`);
      // API returns { states: [...] } object, extract the array
      const statesData = response.data?.states || response.data || [];
      if (!Array.isArray(statesData)) {
        console.error('[ProfileOnboarding] Invalid states data format:', typeof statesData, statesData);
        setStates([]);
      } else {
        // Transform string array to object array if needed
        const normalizedStates = statesData.map(s => 
          typeof s === 'string' ? { name: s, state_id: s } : s
        );
        setStates(normalizedStates);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      setLoadingGeo(prev => ({ ...prev, states: false }));
    }
  };

  const fetchDistricts = async (stateName) => {
    setLoadingGeo(prev => ({ ...prev, districts: true }));
    try {
      const response = await axios.get(`${API_URL}/api/geography/districts/${encodeURIComponent(stateName)}`);
      // API returns { districts: [...] } object, extract the array
      const districtsData = response.data?.districts || response.data || [];
      if (!Array.isArray(districtsData)) {
        console.error('[ProfileOnboarding] Invalid districts data format:', typeof districtsData);
        setDistricts([]);
      } else {
        // Transform string array to object array if needed
        const normalizedDistricts = districtsData.map(d => 
          typeof d === 'string' ? { name: d, district_id: d } : d
        );
        setDistricts(normalizedDistricts);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    } finally {
      setLoadingGeo(prev => ({ ...prev, districts: false }));
    }
  };

  const fetchConstituencies = async (stateName, districtName) => {
    setLoadingGeo(prev => ({ ...prev, constituencies: true }));
    try {
      const response = await axios.get(
        `${API_URL}/api/geography/constituencies/${encodeURIComponent(stateName)}/${encodeURIComponent(districtName)}`
      );
      const constituenciesData = response.data?.constituencies || [];
      const hasData = response.data?.has_data || constituenciesData.length > 0;
      
      if (!Array.isArray(constituenciesData)) {
        console.error('[ProfileOnboarding] Invalid constituencies data format:', typeof constituenciesData);
        setConstituencies([]);
        setHasConstituencyData(false);
      } else {
        // Transform to ensure consistent structure
        const normalizedConstituencies = constituenciesData.map(c => 
          typeof c === 'string' ? { name: c, constituency_id: c } : c
        );
        setConstituencies(normalizedConstituencies);
        setHasConstituencyData(hasData);
      }
    } catch (error) {
      console.error('Error fetching constituencies:', error);
      setConstituencies([]);
      setHasConstituencyData(false);
    } finally {
      setLoadingGeo(prev => ({ ...prev, constituencies: false }));
    }
  };

  const handleCategorySelect = (category) => {
    if (!category.available) {
      setSelectedCategory(category);
      setStep('coming-soon');
    } else {
      setSelectedCategory(category);
      setStep('form');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setProfilePhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const uploadProfilePhoto = async () => {
    if (!profilePhoto) return null;
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', profilePhoto);
      
      const response = await axios.post(`${API_URL}/api/profile/photo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      return response.data.photo_url;
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name?.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!formData.state) {
      toast.error('Please select your state');
      return;
    }
    if (!formData.district) {
      toast.error('Please select your district');
      return;
    }
    // Constituency is required when data is available for the selected district
    if (hasConstituencyData && !formData.constituency) {
      toast.error('Please select your assembly constituency');
      return;
    }
    
    setLoading(true);
    try {
      // Upload photo first if selected
      if (profilePhoto) {
        await uploadProfilePhoto();
      }
      
      // Update profile with form data and account category
      const updateData = {
        name: formData.name.trim(),
        state: formData.state,
        district: formData.district,
        account_category: 'responsible_citizen'
      };
      
      if (formData.constituency) {
        updateData.constituency = formData.constituency;
      }
      
      await axios.put(`${API_URL}/api/profile`, updateData, {
        withCredentials: true
      });
      
      toast.success('Profile completed successfully!');
      onComplete && onComplete();
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const renderCategorySelection = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Choose Your Account Type</h3>
        <p className="text-sm text-gray-500 mt-1">Select how you want to participate on POLIVOX</p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {ACCOUNT_CATEGORIES.map((category) => {
          const Icon = category.icon;
          return (
            <button
              key={category.id}
              data-testid={`onboarding-category-${category.id}`}
              onClick={() => handleCategorySelect(category)}
              className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 hover:shadow-lg ${
                category.available 
                  ? 'border-gray-200 hover:border-blue-400 bg-white' 
                  : 'border-gray-100 bg-gray-50'
              }`}
            >
              {!category.available && (
                <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                  <Clock className="h-3 w-3" />
                  Coming Soon
                </div>
              )}
              
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center mb-3`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              
              <h4 className={`font-semibold ${category.available ? 'text-gray-900' : 'text-gray-500'}`}>
                {category.title}
              </h4>
              <p className={`text-xs mt-1 ${category.available ? 'text-gray-500' : 'text-gray-400'}`}>
                {category.description}
              </p>
              
              {category.available && (
                <div className="mt-3 flex items-center text-blue-600 text-sm font-medium">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Available Now
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderComingSoon = () => (
    <div className="text-center py-8">
      <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${selectedCategory?.color} flex items-center justify-center mx-auto mb-4`}>
        {selectedCategory?.icon && <selectedCategory.icon className="h-8 w-8 text-white" />}
      </div>
      
      <h3 className="text-xl font-semibold text-gray-900 mb-3">
        Something exciting is coming!
      </h3>
      
      <p className="text-gray-500 mb-4 max-w-sm mx-auto">
        We're working on this account option for POLIVOX. Stay connected with us. We'll let you know when it becomes available!
      </p>
      
      <p className="text-gray-600 mb-6 text-sm">
        Thank you for your interest in POLIVOX ❤️
      </p>
      
      <Button
        onClick={() => setStep('category')}
        className="bg-blue-600 text-white hover:bg-blue-700"
        data-testid="onboarding-coming-soon-got-it"
      >
        Got it
      </Button>
    </div>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Complete Your Profile</h3>
        <p className="text-sm text-gray-500">
          {completionStatus?.missing_fields?.length > 0 
            ? `Please provide: ${completionStatus.missing_fields.filter(f => f !== 'account_category').join(', ')}`
            : 'Just a few more details to get started'}
        </p>
      </div>

      {/* Profile Photo */}
      <div className="flex justify-center">
        <div className="relative">
          <div 
            className="w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-blue-400 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            {photoPreview ? (
              <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <Camera className="h-8 w-8 text-gray-400" />
            )}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handlePhotoChange}
            accept="image/*"
            className="hidden"
          />
          <p className="text-xs text-gray-500 text-center mt-2">Add photo (optional)</p>
        </div>
      </div>

      {/* Name */}
      <div>
        <Label htmlFor="name" className="text-sm font-medium">
          Full Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Enter your full name"
          className="mt-1"
          required
        />
      </div>

      {/* State */}
      <div>
        <Label htmlFor="state" className="text-sm font-medium">
          State <span className="text-red-500">*</span>
        </Label>
        <Select
          value={formData.state}
          onValueChange={(value) => setFormData(prev => ({ 
            ...prev, 
            state: value,
            district: '',
            constituency: ''
          }))}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder={loadingGeo.states ? "Loading..." : "Select your state"} />
          </SelectTrigger>
          <SelectContent>
            {Array.isArray(states) && states.length > 0 ? (
              states.map((state) => (
                <SelectItem key={state.state_id || state.name || state} value={state.name || state}>
                  {state.name || state}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="_empty" disabled>
                {loadingGeo.states ? "Loading states..." : "No states available"}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* District */}
      <div>
        <Label htmlFor="district" className="text-sm font-medium">
          District <span className="text-red-500">*</span>
        </Label>
        <Select
          value={formData.district}
          onValueChange={(value) => setFormData(prev => ({ 
            ...prev, 
            district: value,
            constituency: ''
          }))}
          disabled={!formData.state || loadingGeo.districts}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder={
              !formData.state ? "Select state first" :
              loadingGeo.districts ? "Loading..." : 
              "Select your district"
            } />
          </SelectTrigger>
          <SelectContent>
            {Array.isArray(districts) && districts.length > 0 ? (
              districts.map((district) => (
                <SelectItem key={district.district_id || district.name || district} value={district.name || district}>
                  {district.name || district}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="_empty" disabled>
                {loadingGeo.districts ? "Loading districts..." : formData.state ? "No districts available" : "Select state first"}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Assembly Constituency (Required when data available) */}
      <div>
        <Label htmlFor="constituency" className="text-sm font-medium">
          Assembly Constituency {hasConstituencyData ? <span className="text-red-500">*</span> : <span className="text-gray-400">(Optional)</span>}
        </Label>
        <Select
          value={formData.constituency}
          onValueChange={(value) => setFormData(prev => ({ ...prev, constituency: value }))}
          disabled={!formData.district || loadingGeo.constituencies}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder={
              !formData.district ? "Select district first" :
              loadingGeo.constituencies ? "Loading..." : 
              constituencies.length === 0 ? "No constituency data" :
              "Select your constituency"
            } />
          </SelectTrigger>
          <SelectContent>
            {Array.isArray(constituencies) && constituencies.length > 0 ? (
              constituencies.map((const_item) => (
                <SelectItem key={const_item.constituency_id || const_item.name} value={const_item.name}>
                  {const_item.name}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="_empty" disabled>
                {loadingGeo.constituencies ? "Loading constituencies..." : 
                 formData.district ? "No constituency data for this district" : "Select district first"}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        {formData.district && !loadingGeo.constituencies && constituencies.length === 0 && (
          <p className="text-xs text-gray-500 mt-1">Constituency data not yet available for this district</p>
        )}
      </div>

      {/* Account Type Badge */}
      <div className="bg-blue-50 rounded-lg p-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
          <User className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="font-medium text-blue-900 text-sm">Responsible Citizen</p>
          <p className="text-xs text-blue-700">You'll be able to report issues and participate in discussions</p>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white"
        disabled={loading || uploadingPhoto}
      >
        {(loading || uploadingPhoto) ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {uploadingPhoto ? 'Uploading Photo...' : 'Saving...'}
          </>
        ) : (
          <>
            <CheckCircle className="h-4 w-4 mr-2" />
            Complete Profile
          </>
        )}
      </Button>
    </form>
  );

  if (checkingStatus) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="sr-only">
            <DialogTitle>Loading</DialogTitle>
            <DialogDescription>Checking profile status...</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Shield className="h-4 w-4 text-white" />
            </div>
            Welcome to POLIVOX
          </DialogTitle>
          <DialogDescription>
            {step === 'category' && 'Select your account type to get started'}
            {step === 'form' && 'Complete your profile to start participating'}
            {step === 'coming-soon' && 'This account type is not yet available'}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {step === 'category' && renderCategorySelection()}
          {step === 'form' && renderForm()}
          {step === 'coming-soon' && renderComingSoon()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileOnboarding;
